import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const AUTH_STORAGE_KEY = 'lum-auth';

export type UserRole =
  | 'super_admin' | 'manager' | 'lead_agent' | 'board_advisor'
  | 'cosec_officer' | 'meal_officer' | 'auditor' | 'client_viewer';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  profile: {
    firstName: string;
    lastName: string;
    avatar?: string;
    phone?: string;
  };
  preferences: {
    aiProvider: 'openai';
    theme: 'light' | 'dark';
    notificationChannels: string[];
    emailDigest?: boolean;
    dashboardLayout?: string[];
    dashboardGrid?: { version: 1; layouts: Record<string, { i: string; x: number; y: number; w: number; h: number }[]> };
  };
  mfaEnabled: boolean;
  mustChangePassword: boolean;
  organizationId: string;
  organizationBranding?: { primaryColor?: string; accentColor?: string };
}

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  setAuth: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setAuth: (token, user) =>
        set({ accessToken: token, user, isAuthenticated: true, isLoading: false }),

      setUser: (user) => set({ user }),

      setAccessToken: (token) =>
        set({ accessToken: token, isAuthenticated: true, isLoading: false }),

      clearAuth: () => {
        set({ accessToken: null, user: null, isAuthenticated: false, isLoading: false });
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
          window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Auth state is authoritative only after login or a successful refresh probe.
      // Do not persist accessToken, user, or isAuthenticated as session truth.
      partialize: () => ({}),
      merge: (_persistedState, currentState) => currentState,
    }
  )
);
